import Box from "@material-ui/core/Box";
import React, { useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import EmailsTableRedesign from "./components/EmailsTableRedesign";
import HubSpotUploaderRedesign from "./components/HubSpotUploaderRedesign";
import { EmailRegExp } from "../../../constants/regExp";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { SelectOptionType } from "../../../components/elements/Select/Select";
import { emailSampleLink } from "../../../constants/sampleLinks";
import { errorToaster } from "../../../helpers/toasters";
import {
  getFoundEmailsInfo,
  getHubSpotLists,
  setFoundEmailsInfo,
  setHubSpotContactLists,
} from "../../../redux/reducers/campaignToCreate";
import { processEmails } from "../../../utils/processEmails";

type Props = {
  panelMode?: boolean;
};

const PageRoot = styled("div")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const Card = styled(Box)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
}));

const CardBody = styled("div")({
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
});

const TableSection = styled("div")({
  marginTop: 24,
});

const CRMRetargetingSegmentsView: React.FC<Props> = ({ panelMode = false }) => {
  const dispatch = useDispatch();

  const [selectedList, setSelectedList] = useState<any[]>([]);
  const [areEmailsLoading, setAreEmailsLoading] = useState(false);

  const foundEmailsInfo = useSelector((state: ReduxApplicationState) => state.campaignToCreate.foundEmailsInfo);
  const hubSpotLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.hubSpotContactLists);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const isHubSpotListsPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const campaignTemplate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignTemplate);

  const options: SelectOptionType[] =
    hubSpotLists?.lists?.map((list) => ({
      label: list.name,
      value: list.listId.toString(),
      key: list.listId,
    })) ?? [];

  const processValidatedData = (uploadedEmails: string[], fileName: string) => {
    setAreEmailsLoading(true);
    const processedData = processEmails(uploadedEmails, fileName);
    dispatch(setFoundEmailsInfo(processedData));
    setAreEmailsLoading(false);
  };

  const handleListChange = (e: any) => {
    setSelectedList(e.target.value);
  };

  const getDataByCategory = async () => {
    try {
      if (selectedList.length > 0) {
        setAreEmailsLoading(true);
        dispatch(getFoundEmailsInfo(selectedList, setAreEmailsLoading));
      } else {
        errorToaster("Select a List to get Contacts.", 5000);
      }
    } catch (error) {
      console.log("Error in handleGetEmailsInfo: ", error);
      errorToaster(error, 5000);
    }
  };

  useEffect(() => {
    dispatch(setHubSpotContactLists(null));
    if (!campaignTemplate) {
      dispatch(setFoundEmailsInfo(null));
      setSelectedList([]);
    }
    if (selectedAdvertiser?.hubspot_authed) {
      dispatch(getHubSpotLists("contact_lists"));
    }
  }, [dispatch, selectedAdvertiser, campaignTemplate]);

  return (
    <PageRoot>
      <Card>
        <CardBody>
          <HubSpotUploaderRedesign
            processValidatedData={processValidatedData}
            regex={EmailRegExp}
            hubSpotSelectorOptions={options}
            getDataFromHubSpot={getDataByCategory}
            handleOptionChange={handleListChange}
            hubSpotSelectorValue={selectedList}
            areDataFromHubSpotIsLoading={areEmailsLoading}
            sampleLink={emailSampleLink}
          />

          {!isHubSpotListsPending && (
            <TableSection>
              <EmailsTableRedesign
                emailsToHash={foundEmailsInfo?.emailsToHash ?? []}
                areEmailsLoading={areEmailsLoading}
              />
            </TableSection>
          )}
        </CardBody>
      </Card>
    </PageRoot>
  );
};

export default CRMRetargetingSegmentsView;
